import toast from 'react-hot-toast';
import { getErrorMessage } from '@/api';
import {
  isLastMethodConflict,
  type AdminDecorationMethod,
  type PositionDecorationMethodAssignment,
} from '@/api/admin';
import {
  useAdminDecorationMethods,
  useAssignDecorationMethod,
  useSetDefaultDecorationMethod,
  useUnassignDecorationMethod,
} from '@/hooks/useAdminDecorationMethods';

interface PositionDecorationMethodsPanelProps {
  productId: string;
  positionId: string;
  positionName: string;
  assignments: PositionDecorationMethodAssignment[] | undefined;
}

/**
 * Decoration methods available on one print position.
 *
 * Unassigning runs the API's withdrawal: designs that relied on the method
 * as the position default are pinned to it, and a new default is picked.
 * The last method on a position cannot be removed (409). Edits reach the
 * Creator and Direct Sale flows immediately, which the hint at the bottom
 * spells out.
 */
export function PositionDecorationMethodsPanel({
  productId,
  positionId,
  positionName,
  assignments,
}: PositionDecorationMethodsPanelProps) {
  const { data: catalog, isLoading, isError } = useAdminDecorationMethods();
  const assign = useAssignDecorationMethod(productId);
  const unassign = useUnassignDecorationMethod(productId);
  const setDefault = useSetDefaultDecorationMethod(productId);

  const assigned = assignments ?? [];
  const assignedIds = new Set(assigned.map((a) => a.decorationMethodId));
  const defaultId = assigned.find((a) => a.isDefault)?.decorationMethodId;

  // One pending target at a time: a click disables its own row rather than
  // the whole panel, so the admin sees which toggle is in flight.
  // Every in-flight target, not just the first: two overlapping toggles
  // must both stay disabled until their own request settles.
  const pendingMethodIds = new Set(
    [assign, unassign, setDefault]
      .filter((mutation) => mutation.isPending)
      .map((mutation) => mutation.variables?.methodId)
      .filter((id): id is string => typeof id === 'string')
  );

  const handleToggle = async (method: AdminDecorationMethod) => {
    const isAssigned = assignedIds.has(method.id);
    try {
      if (isAssigned) {
        await unassign.mutateAsync({ positionId, methodId: method.id });
        toast.success(`${method.name} removed from ${positionName}`);
      } else {
        await assign.mutateAsync({ positionId, methodId: method.id });
        toast.success(`${method.name} added to ${positionName}`);
      }
    } catch (error) {
      // The API's 409 names position ids; the panel knows the position.
      toast.error(
        isLastMethodConflict(error)
          ? `${method.name} is the last method on ${positionName} and cannot be removed`
          : getErrorMessage(error)
      );
    }
  };

  const handleSetDefault = async (method: AdminDecorationMethod) => {
    try {
      await setDefault.mutateAsync({ positionId, methodId: method.id });
      toast.success(`${method.name} is now the default`);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-1.5">
        <p className="text-sm font-medium text-ink">Decoration methods</p>
        <p className="text-xs text-mute">Loading catalog…</p>
      </div>
    );
  }

  if (isError || !catalog) {
    return (
      <div className="space-y-1.5">
        <p className="text-sm font-medium text-ink">Decoration methods</p>
        <p className="text-xs text-danger">
          Could not load the decoration method catalog.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-ink">Decoration methods</p>

      <ul className="space-y-1">
        {catalog.map((method) => {
          const isAssigned = assignedIds.has(method.id);
          const isDefault = defaultId === method.id;
          const isBusy = pendingMethodIds.has(method.id);

          return (
            <li
              key={method.id}
              className="flex items-center justify-between gap-2 rounded-control px-3 py-2 ring-1 ring-line"
            >
              <label className="flex flex-1 items-center gap-2 text-sm text-ink">
                <input
                  type="checkbox"
                  checked={isAssigned}
                  disabled={isBusy}
                  onChange={() => handleToggle(method)}
                  className="h-4 w-4 accent-primary"
                />
                <span>{method.name}</span>
                <span className="font-mono text-xs text-mute">
                  {method.code}
                </span>
              </label>

              {isAssigned && (
                <label className="flex items-center gap-1 text-xs text-ink-2">
                  <input
                    type="radio"
                    name={`default-method-${positionId}`}
                    checked={isDefault}
                    disabled={isBusy}
                    onChange={() => handleSetDefault(method)}
                    className="h-3.5 w-3.5 accent-primary"
                  />
                  Default
                </label>
              )}
            </li>
          );
        })}
      </ul>

      {assigned.length === 0 ? (
        <p className="text-xs text-mute">
          No methods assigned — this position offers no decoration.
        </p>
      ) : !defaultId ? (
        <p className="rounded-control bg-warning px-3 py-2 text-xs text-ink">
          No default set — the Creator and Direct Sale only see a
          position&apos;s default method, so this position currently offers
          none.
        </p>
      ) : null}

      <p className="text-xs text-mute">
        Takes effect immediately in the Creator and Direct Sale.
      </p>
    </div>
  );
}
